import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { SupplierProfileEmailConfig, useGetSupplierProfilesApi } from './use-get-supplier-profiles-api';

const endpoint = 'api/v1/cms/supplier-profiles';
export const updateSupplierProfileNotificationsEndpointIdentifier = `${endpoint}/notifications/update`;

export interface UpdateSupplierProfileNotificationsBody {
  emailConfig?: SupplierProfileEmailConfig | null;
  whatsappNumbers?: string[];
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  id: number;
  body: UpdateSupplierProfileNotificationsBody;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateSupplierProfileNotificationsApi(options?: Options): ReturnType {
  const { refetch: refetchSupplierProfiles } = useGetSupplierProfilesApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ id, body }: TVariables) => {
    try {
      const { data } = await http.patch(`${endpoint}/${id}/notifications`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/notifications/update`],
    onSuccess: () => {
      refetchSupplierProfiles();

      toast({
        description: 'Notification settings have been updated successfully',
        status: 'success',
      });
    },
    ...options,
  });
}
